"use client";

import {
    convertStateToTranscription,
    Dubbings,
    Voice,
    voices,
    TranscriptionClientState,
    TTSModel,
    ttsModels,
    VideoWorkflowStepName,
    videoWorkflowStepNamesThatRequireCaptions,
    defaultVoice,
    defaultTTSModel,
    voiceDescriptions,
    getVoiceLabel,
} from "@/app/api/[[...route]]/services/ai/ai-video-workflow/types";
import { VideoWorkflowStepProps } from "../videoWorkflowStep";
import { useState } from "react";
import { useVideoWorkflow } from "../videoWorkflowProvider";
import toast from "@/lib/toast";
import { client } from "@/lib/hono";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { SrclangType, getPlatformLanguages } from "@/db/schema";
import { Info, LoaderCircle, Sparkles } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import VoicesPlaygroundModal from "@/app/[locale]/(other)/(funnels)/ai/upload-videos/_components/step-2/voicesPlaygroundModal";
import { Hint } from "@/components/hint";
import { getLanguageLabel, t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";

export default function GenerateState({
    stepName,
    texts,
}: {
    stepName: VideoWorkflowStepName;
    texts: VideoWorkflowStepProps["texts"];
}) {
    const { dir } = useLocale();
    const [loading, setLoading] = useState(false);
    const [transcription, setTranscription] = useState<
        TranscriptionClientState | undefined
    >(undefined);
    const [selectedLanguages, setSelectedLanguages] = useState<SrclangType[]>(
        []
    );
    const [model, setModel] = useState<TTSModel>(defaultTTSModel);
    const [voice, setVoice] = useState<Voice>(defaultVoice);
    const [dubbingInstructions, setDubbingInstructions] = useState("");
    const [text, setText] = useState(""); // for dubbigns playground to share across the 2 instances
    const [translateInteractions, setTranslateInteractions] = useState(false);
    const { videoId, hasCaptions, existingVideoCaptions } = useVideoWorkflow();

    const requireTranscription =
        stepName === "captions" ||
        (videoWorkflowStepNamesThatRequireCaptions.includes(stepName) &&
            !hasCaptions);
    const requireSelectLanguages = (
        ["translations", "dubbings"] as VideoWorkflowStepName[]
    ).includes(stepName);
    const requireDubbings = (["dubbings"] as VideoWorkflowStepName[]).includes(
        stepName
    );

    const platformLanguages = getPlatformLanguages();
    const spokenLanguage = existingVideoCaptions.find(
        (c) => c.isSpokenLanguage
    )?.srclang;
    const languagesWithoutSpokenLanguage = spokenLanguage
        ? platformLanguages.filter((l) => l.code !== spokenLanguage)
        : platformLanguages;

    const generate = async () => {
        if (requireTranscription) {
            if (!transcription?.srclang) {
                toast.error(t("toast.error.generate-captions-no-srclng"), {
                    keyName: "toast.error.generate-captions-no-srclng",
                });
                return;
            }
            if (transcription?.hotwords) {
                const rawParts = transcription.hotwords.split(",");
                const parts = rawParts.map((w) => w.trim());
                const hasEmpty = parts.some((p) => p.length === 0);
                const hasSpaceInside = parts.some((p) => /\s/.test(p)); // e.g. "bird cat"
                if (hasEmpty || hasSpaceInside) {
                    toast.error(t("toast.error.hotwords.invalid-format"), {
                        keyName: "toast.error.hotwords.invalid-format",
                    });
                    return;
                }
            }
        }
        if (requireSelectLanguages && selectedLanguages.length === 0) {
            toast.error(t("toast.error.generate.no-selected-languages"), {
                keyName: "toast.error.generate.no-selected-languages",
            });
            return;
        }

        setLoading(true);

        const stepNames = [stepName] as VideoWorkflowStepName[];

        const resGenerate = await client.api.ai["video-workflow"].$post({
            json: {
                videoId,
                stepNames,
                transcription: requireTranscription
                    ? convertStateToTranscription(
                          transcription!.srclang!,
                          transcription!
                      )
                    : undefined,
                translations:
                    stepName === "translations" ? selectedLanguages : undefined,
                translateInteractions:
                    stepName === "translations"
                        ? translateInteractions
                        : undefined,
                dubbings:
                    stepName === "dubbings"
                        ? {
                              languages: selectedLanguages,
                              model,
                              voice,
                              instructions: dubbingInstructions,
                          }
                        : undefined,
            },
        });

        if (!resGenerate.ok) {
            toast.error(t("toast.error.default"), {
                keyName: "toast.error.default",
            });
            setLoading(false);
        }
    };

    return (
        <>
            <div className="mb-2 flex w-full justify-center items-center">
                <Sparkles className="fill-blue-600 text-blue-600 size-6" />
            </div>
            <h2 className="text-base font-semibold">
                {t(texts.title.keyName, undefined, undefined)}
            </h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
                {t(texts.description.keyName, undefined, undefined)}
            </p>
            {requireSelectLanguages && (
                <p className="mt-2 text-center">
                    {/* <span className="text-sm me-1.5">בחרו שפות לתרגום:</span> */}
                    {languagesWithoutSpokenLanguage.map((language) => {
                        const selected = !!selectedLanguages?.find(
                            (srclang) => srclang === language.code
                        );
                        const hasTranslation = existingVideoCaptions.some(
                            (c) => c.srclang === language.code
                        );

                        return (
                            <button
                                className={cn(
                                    "m-0.5 inline-flex items-center transition rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset",
                                    selected &&
                                        "bg-blue-50 hover:bg-blue-100 text-blue-700 ring-blue-500",
                                    !selected &&
                                        "bg-gray-50 hover:bg-gray-100 text-gray-600 ring-gray-500/10"
                                )}
                                onClick={() => {
                                    if (selected) {
                                        setSelectedLanguages((prev) =>
                                            prev.filter(
                                                (srclang) =>
                                                    srclang !== language.code
                                            )
                                        );
                                    } else {
                                        if (
                                            stepName === "dubbings" &&
                                            !hasTranslation
                                        ) {
                                            toast.error(
                                                t(
                                                    "toast.error.dubbings.translation-required"
                                                ),
                                                {
                                                    keyName:
                                                        "toast.error.dubbings.translation-required",
                                                }
                                            );
                                            return;
                                        }
                                        setSelectedLanguages((prev) => [
                                            ...prev,
                                            language.code,
                                        ]);
                                    }
                                }}
                                key={language.code}
                            >
                                {getLanguageLabel(language.code)}
                            </button>
                        );
                    })}
                </p>
            )}
            {stepName === "translations" && (
                <label className="mt-3 flex flex-row items-center justify-center gap-2 cursor-pointer">
                    <Checkbox
                        checked={translateInteractions}
                        onCheckedChange={(checked) =>
                            setTranslateInteractions(!!checked)
                        }
                    />
                    <span className="text-sm">
                        {t("interactions.translate-with-captions.label")}
                    </span>
                </label>
            )}
            <div className="flex flex-col mt-4 gap-2.5 w-full max-w-[320px]">
                {requireDubbings && (
                    <>
                        <Select
                            value={model}
                            onValueChange={(value: TTSModel) => setModel(value)}
                            dir={dir}
                        >
                            <div className="flex">
                                <div className="shrink-0 flex justify-center items-center bg-muted text-sm h-9 border border-input px-2.5 rounded-s-md">
                                    {t(
                                        "video-sheet.generate.dubbings.model.label",
                                        undefined,
                                        undefined
                                    )}
                                </div>
                                <SelectTrigger className="rounded-s-none border-s-0 h-9">
                                    <SelectValue />
                                </SelectTrigger>
                            </div>
                            <SelectContent>
                                <SelectGroup>
                                    <SelectLabel>
                                        {t(
                                            "video-sheet.generate.dubbings.model.placeholder",
                                            undefined,
                                            undefined
                                        )}
                                    </SelectLabel>
                                    <SelectSeparator />
                                    {ttsModels.map((model) => (
                                        <SelectItem key={model} value={model}>
                                            {model}
                                        </SelectItem>
                                    ))}
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                        <Select
                            value={voice}
                            onValueChange={(value: Voice) => setVoice(value)}
                            dir={dir}
                        >
                            <div className="flex">
                                <div className="shrink-0 flex justify-center items-center bg-muted text-sm h-9 border border-input px-2.5 rounded-s-md">
                                    {t(
                                        "video-sheet.generate.dubbings.voice.label",
                                        undefined,
                                        undefined
                                    )}
                                </div>
                                <SelectTrigger className="rounded-s-none border-s-0 h-9">
                                    <SelectValue />
                                </SelectTrigger>
                            </div>
                            <SelectContent>
                                <SelectGroup>
                                    <SelectLabel>
                                        {t(
                                            "video-sheet.generate.select-voice.menu-label",
                                            undefined,
                                            undefined
                                        )}
                                    </SelectLabel>
                                    <SelectSeparator />
                                    {voices.map((voice) => (
                                        <SelectItem key={voice} value={voice}>
                                            {getVoiceLabel(voice)}
                                        </SelectItem>
                                    ))}
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                        <div className="flex">
                            <div className="shrink-0 flex justify-center items-center bg-muted text-sm h-9 border border-input px-2.5 rounded-s-md">
                                {t(
                                    "video-sheet.generate.dubbings.instructions.label",
                                    undefined,
                                    undefined
                                )}
                            </div>
                            <Input
                                value={dubbingInstructions}
                                onChange={(e) =>
                                    setDubbingInstructions(e.target.value)
                                }
                                className="rounded-s-none border-s-0"
                            />
                        </div>
                        <div>
                            <VoicesPlaygroundModal
                                text={text}
                                setText={setText}
                                dubbings={{
                                    model,
                                    voice,
                                    instructions: dubbingInstructions,
                                    languages: selectedLanguages,
                                }}
                                setDubbings={(dubbingsArg: Dubbings) => {
                                    setModel(dubbingsArg.model);
                                    setVoice(dubbingsArg.voice);
                                    setDubbingInstructions(
                                        dubbingsArg.instructions
                                    );
                                }}
                            />
                        </div>
                    </>
                )}
                {requireTranscription && (
                    <>
                        <Select
                            value={transcription?.srclang}
                            onValueChange={(value: SrclangType) =>
                                setTranscription((prev) => ({
                                    ...prev,
                                    srclang: value,
                                }))
                            }
                            dir={dir}
                        >
                            <div className="flex">
                                <div className="shrink-0 flex justify-center items-center bg-muted text-sm h-9 border border-input px-2.5 rounded-s-md">
                                    {t(
                                        "video-sheet.generate.captions.spoken-language.label",
                                        undefined,
                                        undefined
                                    )}
                                </div>

                                <SelectTrigger className="rounded-s-none border-s-0 h-9">
                                    <SelectValue />
                                </SelectTrigger>
                            </div>
                            <SelectContent>
                                <SelectGroup>
                                    <SelectLabel>
                                        {t(
                                            "video-sheet.captions-tab.generate.select-language.menu-label",
                                            undefined,
                                            undefined
                                        )}
                                    </SelectLabel>
                                    <SelectSeparator />
                                    {platformLanguages.map((l) => (
                                        <SelectItem key={l.code} value={l.code}>
                                            {getLanguageLabel(l.code)}
                                        </SelectItem>
                                    ))}
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                        <div className="flex">
                            <div className="shrink-0 flex justify-center items-center bg-muted text-sm h-9 border border-input px-2.5 rounded-s-md">
                                <Hint
                                    tooltipClassName="max-w-[300px] text-center"
                                    label={t(
                                        "video-sheet.generate.captions.context.hint",
                                        undefined,
                                        undefined
                                    )}
                                >
                                    <div
                                        className={cn(
                                            buttonVariants({
                                                size: "icon",
                                                variant: "ghost",
                                            }),
                                            "size-7"
                                        )}
                                    >
                                        <Info className="size-4" />
                                    </div>
                                </Hint>
                                <span className="ms-0.5">
                                    {t(
                                        "video-sheet.generate.captions.context.label",
                                        undefined,
                                        undefined
                                    )}
                                </span>
                            </div>
                            <Input
                                placeholder={t(
                                    "video-sheet.generate.captions.context.placeholder",
                                    undefined,
                                    undefined
                                )}
                                value={transcription?.initialPrompt || ""}
                                onChange={(e) =>
                                    setTranscription((prev) => ({
                                        ...prev,
                                        initialPrompt: e.target.value,
                                    }))
                                }
                                className="rounded-s-none border-s-0"
                            />
                        </div>
                        <div className="flex">
                            <div className="shrink-0 flex justify-center items-center bg-muted text-sm h-9 border border-input px-2.5 rounded-s-md">
                                <Hint
                                    tooltipClassName="max-w-[300px] text-center"
                                    label={t(
                                        "video-sheet.generate.captions.hotwords.hint",
                                        undefined,
                                        undefined
                                    )}
                                >
                                    <div
                                        className={cn(
                                            buttonVariants({
                                                size: "icon",
                                                variant: "ghost",
                                            }),
                                            "size-7"
                                        )}
                                    >
                                        <Info className="size-4" />
                                    </div>
                                </Hint>
                                <span className="ms-0.5">
                                    {t(
                                        "video-sheet.generate.captions.hotwords.label",
                                        undefined,
                                        undefined
                                    )}
                                </span>
                            </div>
                            <Input
                                placeholder={t(
                                    "video-sheet.generate.captions.hotwords.placeholder",
                                    undefined,
                                    undefined
                                )}
                                value={transcription?.hotwords || ""}
                                onChange={(e) =>
                                    setTranscription((prev) => ({
                                        ...prev,
                                        hotwords: e.target.value,
                                    }))
                                }
                                className="rounded-s-none border-s-0"
                            />
                        </div>
                    </>
                )}

                <Button
                    variant="ai"
                    size="default"
                    onClick={generate}
                    className="mt-1 flex flex-row gap-2 items-center"
                >
                    {loading ? (
                        <LoaderCircle className="size-4 animate-spin" />
                    ) : (
                        t(texts.generateBtn.keyName, undefined, undefined)
                    )}
                </Button>
            </div>
        </>
    );
}
